// Single HTTP transport for the SPA. Web: window.fetch, untouched. Native
// (Capacitor): EXPLICIT CapacitorHttp.request for every backend call.
//
// Why explicit instead of the plugin's global fetch patch: with the patch
// enabled the login flow died on prod — GET /auth/csrf went out through the
// WebView stack (browser UA + Referer) while POST /auth/login went native
// (Dalvik UA). Two stacks = two cookie jars, so the Flask session cookie
// holding the CSRF pair never accompanied the POST → permanent 419. Routing
// every call through CapacitorHttp.request keeps ONE native cookie jar
// (persists across app restarts; SameSite doesn't apply, no CORS).
import { API_BASE, IS_NATIVE } from './native'

/** The subset of Response the API layers use (fetch's Response satisfies it).
 *  json() is `any` to match fetch's Response.json() — callers already cast. */
export interface ResponseLike {
  status: number
  ok: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json(): Promise<any>
}

export async function appFetch(url: string, init: RequestInit = {}): Promise<ResponseLike> {
  if (!IS_NATIVE) return fetch(url, init)
  // Dynamic import keeps @capacitor/core out of the web bundle.
  const { CapacitorHttp } = await import('@capacitor/core')
  const headers: Record<string, string> = {}
  new Headers(init.headers).forEach((v, k) => { headers[k] = v })
  // Flask-WTF's WTF_CSRF_SSL_STRICT demands a same-origin Referer on every
  // HTTPS unsafe request (browsers always send one; the native stack never
  // does → permanent 419). We ARE the first party, so say so explicitly.
  headers['Referer'] = API_BASE + '/'
  const res = await CapacitorHttp.request({
    url,
    method: (init.method ?? 'GET').toUpperCase(),
    headers,
    // api()/createRender only ever send JSON strings; pass through verbatim.
    data: typeof init.body === 'string' ? init.body : undefined,
  })
  return {
    status: res.status,
    ok: res.status >= 200 && res.status < 300,
    json: async () => (typeof res.data === 'string' ? JSON.parse(res.data) : res.data),
  }
}
